import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import UserContext from '../../../../components/User_Context.js';
import * as menu from "../../../../assets/menus/menu.js";
import * as breadcrumb from "../../../../components/ui/breadcrumb.js";
import * as api from "../../../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import { formBuilderLabels } from '../../../_label/form_builder/form_builder_labels.js';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import Form_Compiler from '../compilerNew.js';
import {calculateHeaderHeight} from '../../../../components/ui_utils.js';
import Choices from '../../../../components/Choices.js';

const Validation_Dashboard = () => {
    const { currentUser } = useContext(UserContext);
    const [assignments, setAssignments] = useState(null);
    const [activeTab, setActiveTab] = useState('Completed'); // 'Completed' or 'Approved'
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { t } = useTranslation();
    const MySwal = withReactContent(Swal);
    const [headerHeight, setHeaderHeight] = useState(0);
    
    // Form viewing state
    const [viewingForm, setViewingForm] = useState(null);
    const [viewingFormData, setViewingFormData] = useState(null);
    
    // User and Group selection state
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedUser, setSelectedUser] = useState("");
    const [selectedGroup, setSelectedGroup] = useState("");
    const [filterType, setFilterType] = useState('user'); // 'user' or 'group'
    const [selectedUserObject, setSelectedUserObject] = useState(null);
    const [selectedGroupObject, setSelectedGroupObject] = useState(null);

    // Initialize translations and setup
    useEffect(() => {
        setHeaderHeight(calculateHeaderHeight());
        
        const i18n = menu.getI18n();
        i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
        i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);

        menu.write({ user: currentUser() });

        // Set breadcrumb
        breadcrumb.set([
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/validation_dashboard", name: t('validationDashboard', { ns: 'labels' }) }
        ]);

        loadUsersAndGroups();
    }, []);

    // Load users and groups for filtering
    const loadUsersAndGroups = async () => {
        try {
            setLoading(true);
            setError(null);

            // Load users
            const usersResult = await api.sendMessage("admin/security/getAllUsers", {
                user: currentUser(),
                body: {}
            });

            if (usersResult?.success && usersResult?.users) {
                // Transform users data to match Choices component format
                const formattedUsers = usersResult.users.map(user => {
                    // Build display name with name, surname and email
                    const name = user.Name || '';
                    const surname = user.LastName || '';
                    const email = user.EMail || '';
                    
                    // Format display name: "Name Surname (email)"
                    let displayName = name;
                    if (surname) {
                        displayName += ` ${surname}`;
                    }
                    if (email) {
                        displayName += ` (${email})`;
                    }
                    
                    return {
                        Code: user.Id,
                        Name: displayName || email || user.Username || 'ANONYMOUS',
                        OriginalUser: user // Keep the original user object
                    };
                });
                setUsers(formattedUsers);
            }

            // Load groups
            const groupsResult = await api.sendMessage("admin/security/getGroups", {
                user: currentUser(),
                body: {}
            });

            if (groupsResult?.success && groupsResult?.groups) {
                const formattedGroups = groupsResult.groups.map(group => ({
                    Code: group.Id,
                    Name: group.Name || 'Unnamed Group',
                    OriginalGroup: group
                }));
                setGroups(formattedGroups);
            }
        } catch (error) {
            console.error("Error loading users and groups:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Load dashboard data - completed and approved assignments with filters
    const loadDashboardData = async () => {
        // Check if user or group is selected
        if (filterType === 'user' && !selectedUserObject) {
            MySwal.fire({
                title: t('attention', { ns: 'labels' }),
                text: t('pleaseSelectUser', { ns: 'labels', defaultValue: 'Seleziona un utente per visualizzare i dati' }),
                icon: 'warning'
            });
            return;
        }
        if (filterType === 'group' && !selectedGroupObject) {
            MySwal.fire({
                title: t('attention', { ns: 'labels' }),
                text: t('pleaseSelectGroup', { ns: 'labels', defaultValue: 'Seleziona un gruppo per visualizzare i dati' }),
                icon: 'warning'
            });
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const body = {
                validation: true
            };
            
            // Add user or group filter
            if (filterType === 'user' && selectedUserObject) {
                body.validationUserId = selectedUserObject.Id || selectedUserObject.id || selectedUserObject.ID;
            } else if (filterType === 'group' && selectedGroupObject) {
                body.validationGroupId = selectedGroupObject.Id;
            }

            const result = await api.sendMessage("darm/formAssignments/getDashboardData", {
                user: currentUser(),
                body: body
            });

            if (result && result.success) {
                setAssignments(result.assignments || []);
            } else {
                throw new Error(result?.error || 'Failed to load dashboard data');
            }
        } catch (error) {
            console.error("Error loading dashboard data:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Filter assignments based on active tab
    const getFilteredAssignments = () => {
        if (!assignments) return [];
        return assignments.filter(assignment => assignment.Status === activeTab);
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format date and time for display
    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get status badge color
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Saved': return 'bg-warning text-dark';
            case 'Compiled': return 'bg-info text-white';
            case 'Approved': return 'bg-success text-white';
            default: return 'bg-secondary text-white';
        }
    };

    // Handle form validation
    const handleValidateForm = async (completionId, formName) => {
        MySwal.fire({
            title: t('attention', { ns: 'labels' }),
            text: t('confirmValidation', { ns: 'labels' }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('confirm', { ns: 'labels' }),
            cancelButtonText: t('cancel', { ns: 'labels' })
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await api.sendMessage("darm/formCompletion/validate", {
                        user: currentUser(),
                        body: {
                            completionId: completionId
                        }
                    });

                    if (response.success) {
                        MySwal.fire({
                            title: t('success', { ns: 'labels' }),
                            text: t('validationSuccess', { ns: 'labels' }),
                            icon: 'success',
                            timer: 2000
                        });
                        // Refresh the dashboard data
                        loadDashboardData();
                    } else {
                        MySwal.fire({
                            title: t('error', { ns: 'labels' }),
                            text: response.message || t('validationError', { ns: 'labels' }),
                            icon: 'error'
                        });
                    }
                } catch (error) {
                    MySwal.fire({
                        title: t('error', { ns: 'labels' }),
                        text: t('validationError', { ns: 'labels' }),
                        icon: 'error'
                    });
                }
            }
        });
    };

    // Handle viewing a completed form in read-only mode
    const handleViewCompletion = async (assignment, completion) => {
        try {
            // Show loading indicator
            MySwal.fire({
                title: t('loading', { ns: 'labels' }),
                text: t('loadingFormData', { ns: 'labels', defaultValue: 'Loading form data...' }),
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Load both the form completion data AND the form structure
            const [completionResult, formResult] = await Promise.all([
                // Load the form completion data
                api.sendMessage("darm/formCompletion/get", {
                    user: currentUser(),
                    body: {
                        formId: assignment.FormId,
                        completionId: completion.Id,
                        validation: true
                    }
                }),
                // Load the form structure
                api.sendMessage("darm/formBuilder/get", {
                    user: currentUser(),
                    body: {
                        id: assignment.FormId
                    }
                })
            ]);

            MySwal.close();

            if (completionResult?.success && completionResult?.formCompletion && formResult?.success && formResult?.form) {
                // Combine the form structure with the completion data
                const combinedData = {
                    ...completionResult.formCompletion,
                    form: formResult.form
                };
                
                console.log("🔍 [DEBUG] Combined form data:", combinedData);
                
                // Set the viewing form data
                setViewingForm(assignment);
                setViewingFormData(combinedData);
                
                // Update breadcrumb when viewing a form
                updateBreadcrumbForFormView(assignment.FormName || 'Form');
            } else {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: completionResult?.message || formResult?.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form data' }),
                    icon: 'error'
                });
            }
        } catch (error) {
            MySwal.close();
            console.error("Error loading form for viewing:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form data' }),
                icon: 'error'
            });
        }
    };

    // Handle closing the form viewer
    const handleCloseFormViewer = () => {
        setViewingForm(null);
        setViewingFormData(null);
        // Reset breadcrumb to dashboard
        setBreadCrumb();
    };

    // Update breadcrumb when viewing a form
    const updateBreadcrumbForFormView = (formName) => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/validation_dashboard", name: t('validationDashboard', { ns: 'labels' }) },
            { link: "#", name: `${t('viewingForm', { ns: 'labels', defaultValue: 'Viewing Form' })}: ${formName}` }
        ];
        breadcrumb.set(path);
    };

    // Set breadcrumb for the dashboard
    const setBreadCrumb = () => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/validation_dashboard", name: t('validationDashboard', { ns: 'labels' }) }
        ];
        breadcrumb.set(path);
    };

    // Assignment Card Component for Validation
    const ValidationAssignmentCard = ({ assignment }) => {
        const [expanded, setExpanded] = useState(false);

        return (
            <div className="card mb-4 shadow-sm h-auto" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                {/* Card Header */}
                <div className="card-header border-0 position-relative" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px 12px 0 0' }}>
                    <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-2">
                            <h4 className="card-title mb-0 fw-bold text-dark me-3">
                                {assignment.FormName}
                            </h4>
                            {assignment.FormCode && (
                                <span className="badge bg-light text-dark border">{assignment.FormCode}</span>
                            )}
                            <span className={`badge ms-2 ${assignment.Status === 'Approved' ? 'bg-primary' : 'bg-success'}`}>
                                <i className={`fas ${assignment.Status === 'Approved' ? 'fa-thumbs-up' : 'fa-check-circle'} me-1`}></i>
                                {t(assignment.Status, { ns: 'labels' })}
                            </span>
                        </div>
                        
                        <div className="d-flex flex-wrap gap-2 mb-2">
                            {assignment.FormGroup && (
                                <span className="badge bg-primary">{assignment.FormGroup}</span>
                            )}
                            {assignment.GroupName && (
                                <span className="badge bg-info">
                                    <i className="fas fa-users me-1"></i>
                                    {assignment.GroupName}
                                </span>
                            )}
                        </div>

                        <div className="d-flex flex-wrap gap-3">
                            <small className="text-muted">
                                <i className="fas fa-calendar me-1"></i>
                                {t('createdDate', { ns: 'labels' })}: {formatDate(assignment.ItemCreated)}
                            </small>
                            
                            {(assignment.StartDate || assignment.EndDate) && (
                                <small className="text-muted">
                                    <i className="fas fa-clock me-1"></i>
                                    {t('assignmentPeriod', { ns: 'labels' })}: 
                                    {assignment.StartDate && ` ${formatDate(assignment.StartDate)}`}
                                    {assignment.StartDate && assignment.EndDate && ' - '}
                                    {assignment.EndDate && ` ${formatDate(assignment.EndDate)}`}
                                </small>
                            )}
                            
                            {assignment.ToValidate && (
                                <small className="text-warning">
                                    <i className="fas fa-shield-alt me-1"></i>
                                    {t('requiresValidation', { ns: 'labels' })}
                                </small>
                            )}
                        </div>
                    </div>
                    
                    {/* Toggle button */}
                    <div className="position-absolute" style={{ bottom: '1rem', right: '1rem' }}>
                        <a
                            className="text-secondary fs-13"
                            title={expanded ? t('hide', { ns: 'labels' }) + ' ' + t('details', { ns: 'labels' }) : t('show', { ns: 'labels' }) + ' ' + t('details', { ns: 'labels' })}
                            role="button"
                            onClick={() => setExpanded(!expanded)}
                            style={{ 
                                display: 'inline-block',
                                padding: '2px 4px',
                                minWidth: '30px',
                                cursor: 'pointer'
                            }}
                        >
                            <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} fa-lg`}></i>
                        </a>
                    </div>
                </div>

                {/* Card Body - Details */}
                <div className="card-body">
                    {/* Assignment Info Grid */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <div className="text-center p-4 bg-white border rounded-3 shadow-sm">
                                <div className="fs-2 fw-bold text-success mb-1">{assignment.completedCycles}</div>
                                <div className="text-muted small fw-medium">{t('completedCycles', { ns: 'labels' })}</div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="text-center p-4 bg-white border rounded-3 shadow-sm">
                                <div className="fs-2 fw-bold text-primary mb-1">{assignment.remainingCycles}</div>
                                <div className="text-muted small fw-medium">{t('remainingCycles', { ns: 'labels' })}</div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="text-center p-4 border rounded-3 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                                <div className="fs-2 fw-bold text-secondary mb-1">{assignment.totalCycles}</div>
                                <div className="text-muted small fw-medium">{t('totalCycles', { ns: 'labels' })}</div>
                            </div>
                        </div>
                    </div>

                    {/* Completions List */}
                    {expanded && (
                        <div className="mt-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h6 className="mb-0 text-secondary">
                                    <i className="fas fa-list me-2"></i>
                                    {t('formCompletions', { ns: 'labels' })}
                                </h6>
                                <span className="text-muted fw-medium">({assignment.completions?.length || 0})</span>
                            </div>
                            
                            {assignment.completions && assignment.completions.length > 0 ? (
                                <div className="list-group list-group-flush">
                                    {assignment.completions.map((completion, index) => (
                                        <div 
                                            key={completion.Id} 
                                            className="list-group-item border-0 rounded-3 mb-2 shadow-sm"
                                            style={{ 
                                                backgroundColor: completion.countsAsCycle ? '#f8fff8' : '#fffaf0',
                                                border: completion.countsAsCycle ? '1px solid #d4edda' : '1px solid #ffeaa7'
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center py-2">
                                                <div className="d-flex align-items-center">
                                                    <span className="badge me-3" style={{ 
                                                        backgroundColor: completion.countsAsCycle ? '#28a745' : '#6c757d', 
                                                        color: 'white',
                                                        minWidth: '35px'
                                                    }}>
                                                        #{index + 1}
                                                    </span>
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            <span className={`badge ${getStatusBadgeColor(completion.Status)}`}>
                                                                {t(completion.Status, { ns: 'labels' })}
                                                            </span>
                                                            {completion.countsAsCycle && (
                                                                <span className="badge bg-success">
                                                                    <i className="fas fa-star me-1"></i>
                                                                    {t('completedCycle', { ns: 'labels' })}
                                                                </span>
                                                            )}
                                                            {completion.Validated && (
                                                                <span className="badge bg-warning">
                                                                    <i className="fas fa-check-circle me-1"></i>
                                                                    {t('validated', { ns: 'labels' })}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <small className="text-muted">
                                                            <i className="fas fa-user me-1"></i>
                                                            {completion.Compiler}
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="text-end">
                                                    <div className="d-flex gap-2 mb-2">
                                                        {/* View button for compiled forms */}
                                                        {((completion.Status === 'Compilata' || completion.Status === 'Compiled') || 
                                                          (completion.Status === 'Approvata' || completion.Status === 'Approved')) && (
                                                            <button
                                                                className="btn btn-sm btn-info"
                                                                onClick={() => handleViewCompletion(assignment, completion)}
                                                                title={t('viewForm', { ns: 'labels' })}
                                                            >
                                                                <i className="fa-solid fa-eye me-1"></i>
                                                                {t('view', { ns: 'labels' })}
                                                            </button>
                                                        )}
                                                        
                                                        {/* Validate button - only for Completed assignments, not for Approved ones */}
                                                        {(completion.Status === 'Compilata' || completion.Status === 'Compiled') && 
                                                         assignment.Status === 'Completed' && assignment.ToValidate && !completion.Validated && (
                                                            <button
                                                                className="btn btn-sm btn-success"
                                                                onClick={() => handleValidateForm(completion.Id, assignment.FormName)}
                                                                title={t('validate', { ns: 'labels' })}
                                                            >
                                                                <i className="fa-solid fa-check me-1"></i>
                                                                {t('validate', { ns: 'labels' })}
                                                            </button>
                                                        )}
                                                    </div>
                                                    
                                                    {assignment.ToValidate && completion.Validated && (
                                                        <div className="text-success small mb-1">
                                                            <i className="fas fa-check-circle me-1"></i>
                                                            {t('validated', { ns: 'labels' })}
                                                            {completion.ValidatedBy && (
                                                                <div className="text-muted">{completion.ValidatedBy}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    <small className="text-muted">
                                                        <i className="fas fa-clock me-1"></i>
                                                        {formatDateTime(completion.ItemCreated)}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center text-muted p-5 bg-light rounded-3">
                                    <i className="fas fa-inbox fa-3x mb-3 opacity-50"></i>
                                    <div className="fw-medium">{t('noCompletionsYet', { ns: 'labels' })}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // If viewing a form, show the Form_Compiler in read-only mode
    if (viewingForm && viewingFormData) {
        return (
            <div className="container-fluid">
                <Form_Compiler
                    form={viewingFormData.form}
                    formCompletion={viewingFormData}
                    language="it"
                    readOnly={true}
                    onClose={handleCloseFormViewer}
                    colors={{
                        primary: '#f05f23',
                        primaryHover: '#d44d12',
                        primaryLight: '#fef0ea',
                        secondary: '#6c757d',
                        secondaryLight: '#e9ecef',
                        dark: '#343a40',
                        light: '#f8f9fa',
                        white: '#ffffff',
                        success: '#28a745',
                        danger: '#dc3545',
                        warning: '#ffc107',
                        info: '#17a2b8'
                    }}
                />
            </div>
        );
    }

    return (
        <div className="container-fluid" style={{ height: `calc(100vh - ${headerHeight}px)` }}>
            {/* Fixed Header */}
            <header className="bg-white shadow-sm" style={{ position: 'fixed', top: `${headerHeight}px`, left: '240px', right: 0, zIndex: 1000 }}>
                <div className="container-fluid d-flex justify-content-between align-items-center py-2">
                    {/* Breadcrumb */}
                    <ol className="breadcrumb mb-0"></ol>
                    
                    {/* Action buttons */}
                    <div className="d-flex gap-3">
                                                                <a 
                                            className="text-primary fs-13" 
                                            onClick={() => loadDashboardData()}
                                            role="button" 
                                            title={t('refresh', { ns: 'labels' })}
                                        >
                                            <i className="fa-solid fa-sync fa-2x"></i>
                                        </a>
                    </div>
                </div>
            </header>

            {/* Scrollable Content */}
            <main className="overflow-auto" style={{ paddingTop: '60px', height: '100%', backgroundColor: '#f4f6f9' }}>
                <div className="container-fluid py-3">
                    <div className="row">
                        <div className="col-12">
                            <div className="card h-auto">
                                {/* Dashboard Header */}
                                <div className="card-header bg-white border-0 pb-3">
                                    <div>
                                        <h3 className="fw-bold mb-2 text-dark">{t('validationDashboard', { ns: 'labels' })}</h3>
                                        <p className="mb-0 text-muted">
                                            {t('validationDashboardDescription', { ns: 'labels', defaultValue: 'Gestisci la validazione dei form completati.' })}
                                        </p>
                                    </div>
                                </div>

                                <div className="card-body p-0">
                                    {/* Filter Section */}
                                    <div className="border-bottom bg-white p-4">
                                        <div className="row align-items-end">
                                            <div className="col-md-12">
                                                <div className="alert alert-info mb-3">
                                                    <i className="fa-solid fa-info-circle me-2"></i>
                                                    {t('validationFilterInfo', { ns: 'labels', defaultValue: 'Seleziona un utente o gruppo per visualizzare le assegnazioni da validare.' })}
                                                </div>

                                                {/* Filter Type Tabs */}
                                                <ul className="nav nav-tabs" id="filter-tab" role="tablist">
                                                    <li className="nav-item" role="presentation">
                                                        <button 
                                                            className={`nav-link ${filterType === 'user' ? 'active' : ''}`} 
                                                            id="filter-user-tab" 
                                                            onClick={() => {
                                                                setFilterType('user');
                                                                setSelectedGroup('');
                                                                setSelectedGroupObject(null);
                                                                setAssignments(null);
                                                            }} 
                                                            type="button" 
                                                            role="tab" 
                                                            aria-controls="filter-user-tab-pane" 
                                                            aria-selected={filterType === 'user'}
                                                        >
                                                            {t('filterByUser', { ns: 'labels', defaultValue: 'Filtra per Utente' })}
                                                        </button>
                                                    </li>
                                                    <li className="nav-item" role="presentation">
                                                        <button 
                                                            className={`nav-link ${filterType === 'group' ? 'active' : ''}`} 
                                                            id="filter-group-tab" 
                                                            onClick={() => {
                                                                setFilterType('group');
                                                                setSelectedUser('');
                                                                setSelectedUserObject(null);
                                                                setAssignments(null);
                                                            }} 
                                                            type="button" 
                                                            role="tab" 
                                                            aria-controls="filter-group-tab-pane" 
                                                            aria-selected={filterType === 'group'}
                                                        >
                                                            {t('filterByGroup', { ns: 'labels', defaultValue: 'Filtra per Gruppo' })}
                                                        </button>
                                                    </li>
                                                </ul>

                                                {/* Tab Content */}
                                                <div className="tab-content pt-3" id="filter-tab-content">
                                                    <div className={`tab-pane fade ${filterType === 'user' ? 'show active' : ''}`} id="filter-user-tab-pane" role="tabpanel" aria-labelledby="filter-user-tab">
                                                        <label className="form-label">
                                                            {t('selectUser', { ns: 'labels' })}
                                                            <span className="text-danger ms-1">*</span>
                                                        </label>
                                                        <Choices
                                                            voc={users}
                                                            value="Code"
                                                            text="Name"
                                                            className="inputStyle blackText bg-white"
                                                            selected={selectedUser ? [{ Code: selectedUser }] : []}
                                                            onChange={(e) => {
                                                                const selected = e.selected?.[0]?.Code || "";
                                                                const selectedUserData = users.find(user => user.Code === selected);
                                                                setSelectedUser(selected);
                                                                setSelectedUserObject(selectedUserData?.OriginalUser || null);
                                                                setAssignments(null); // Clear previous results
                                                            }}
                                                            placeholder={t('selectUser', { ns: 'labels' })}
                                                            dropDownParent="root"
                                                        />
                                                    </div>
                                                    <div className={`tab-pane fade ${filterType === 'group' ? 'show active' : ''}`} id="filter-group-tab-pane" role="tabpanel" aria-labelledby="filter-group-tab">
                                                        <label className="form-label">
                                                            {t('selectGroup', { ns: 'labels' })}
                                                            <span className="text-danger ms-1">*</span>
                                                        </label>
                                                        <Choices
                                                            voc={groups}
                                                            value="Code"
                                                            text="Name"
                                                            className="inputStyle blackText bg-white"
                                                            selected={selectedGroup ? [{ Code: selectedGroup }] : []}
                                                            onChange={(e) => {
                                                                const selected = e.selected?.[0]?.Code || "";
                                                                const selectedGroupData = groups.find(group => group.Code === selected);
                                                                setSelectedGroup(selected);
                                                                setSelectedGroupObject(selectedGroupData?.OriginalGroup || null);
                                                                setAssignments(null); // Clear previous results
                                                            }}
                                                            placeholder={t('selectGroup', { ns: 'labels' })}
                                                            dropDownParent="root"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Load Button */}
                                                <div className="col-12 mt-4">
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={loadDashboardData}
                                                        disabled={loading}
                                                    >
                                                        {loading ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                {t('loading', { ns: 'labels' })}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fa-solid fa-search me-2"></i>
                                                                {t('loadData', { ns: 'labels', defaultValue: 'Carica Dati' })}
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Modern Tabs - Only show if data is loaded */}
                                    {assignments && (
                                        <div className="d-flex border-bottom bg-white">
                                        <button 
                                            className={`btn btn-light flex-grow-1 text-center border-0 rounded-0 ${activeTab === 'Completed' ? 'border-bottom border-warning border-3' : ''}`}
                                            onClick={() => handleTabChange('Completed')}
                                        >
                                            <div className="d-flex align-items-center justify-content-center">
                                                <i className="fas fa-check-circle me-2"></i>
                                                <span>{t('completedAssignments', { ns: 'labels' })}</span>
                                                {assignments && activeTab === 'Completed' && (
                                                    <span className="badge ms-2 bg-warning text-dark">
                                                        {assignments.filter(a => a.Status === 'Completed').length}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                        <button 
                                            className={`btn btn-light flex-grow-1 text-center border-0 rounded-0 ${activeTab === 'Approved' ? 'border-bottom border-success border-3' : ''}`}
                                            onClick={() => handleTabChange('Approved')}
                                        >
                                            <div className="d-flex align-items-center justify-content-center">
                                                <i className="fas fa-thumbs-up me-2"></i>
                                                <span>{t('approvedAssignments', { ns: 'labels', defaultValue: 'Assegnazioni Approvate' })}</span>
                                                {assignments && activeTab === 'Approved' && (
                                                    <span className="badge ms-2 bg-success">
                                                        {assignments.filter(a => a.Status === 'Approved').length}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                        </div>
                                    )}

                                    {/* Tab Content */}
                                    <div className="p-4">
                                        {loading && (
                                            <div className="d-flex justify-content-center py-5">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">{t('loading', { ns: 'labels' })}</span>
                                                </div>
                                            </div>
                                        )}

                                        {error && (
                                            <div className="alert alert-danger">
                                                <strong>{t('error', { ns: 'labels' })}:</strong> {error}
                                            </div>
                                        )}

                                        {!loading && !error && !assignments && (
                                            <div className="text-center py-5">
                                                <i className="fas fa-filter fa-3x text-muted mb-3"></i>
                                                <h5 className="text-muted">
                                                    {t('noDataLoaded', { ns: 'labels', defaultValue: 'Nessun dato caricato' })}
                                                </h5>
                                                <p className="text-muted">
                                                    {t('selectUserOrGroupToStart', { ns: 'labels', defaultValue: 'Seleziona un utente o gruppo e clicca "Carica Dati" per iniziare.' })}
                                                </p>
                                            </div>
                                        )}

                                        {!loading && !error && assignments && (
                                            <>
                                                {getFilteredAssignments().length > 0 ? (
                                                    <div className="row">
                                                        <div className="col-12">
                                                            {getFilteredAssignments().map((assignment) => (
                                                                <ValidationAssignmentCard 
                                                                    key={assignment.Id} 
                                                                    assignment={assignment} 
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-5">
                                                        <i className={`fas ${activeTab === 'Completed' ? 'fa-check-circle' : 'fa-thumbs-up'} fa-3x text-muted mb-3`}></i>
                                                        <h5 className="text-muted">
                                                            {activeTab === 'Completed' 
                                                                ? t('noCompletedAssignments', { ns: 'labels' })
                                                                : t('noApprovedAssignments', { ns: 'labels', defaultValue: 'Nessuna assegnazione approvata' })
                                                            }
                                                        </h5>
                                                        <p className="text-muted">
                                                            {activeTab === 'Completed'
                                                                ? t('noCompletedAssignmentsForValidation', { 
                                                                    ns: 'labels', 
                                                                    defaultValue: 'Non ci sono assegnazioni completate che richiedono validazione.' 
                                                                })
                                                                : t('noApprovedAssignmentsDescription', {
                                                                    ns: 'labels',
                                                                    defaultValue: 'Non ci sono assegnazioni approvate da visualizzare.'
                                                                })
                                                            }
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Validation_Dashboard;
